import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../ui/button';
import {
  LayoutDashboard,
  Package,
  FolderTree,
  ShoppingCart,
  Users,
  CreditCard,
  Truck,
  Tag,
  Star,
  FileText,
  Newspaper,
  Image,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Gift,
  Share2,
  File,
  BarChart3,
  ImageIcon,
} from 'lucide-react';
import { Toaster } from '../ui/sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_841fbfa7-1a20-4a56-bfc0-aa4a083c4873/artifacts/mbzov694_Logo-Rani_final.webp';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/rani-admin' },
  { icon: Package, label: 'Products', path: '/rani-admin/products' },
  { icon: FolderTree, label: 'Categories', path: '/rani-admin/categories' },
  { icon: ShoppingCart, label: 'Orders', path: '/rani-admin/orders' },
  { icon: Users, label: 'Customers', path: '/rani-admin/customers' },
  { icon: CreditCard, label: 'Payments', path: '/rani-admin/payments' },
  { icon: Gift, label: 'Loyalty', path: '/rani-admin/loyalty' },
  { icon: Share2, label: 'Referrals', path: '/rani-admin/referrals' },
  { icon: Tag, label: 'Coupons', path: '/rani-admin/coupons' },
  { icon: Star, label: 'Reviews', path: '/rani-admin/reviews' },
  { icon: FileText, label: 'Pages', path: '/rani-admin/pages' },
  { icon: File, label: 'Footer Pages', path: '/rani-admin/footer-pages' },
  { icon: Newspaper, label: 'Blog', path: '/rani-admin/blog' },
  { icon: Image, label: 'Sliders', path: '/rani-admin/sliders' },
  { icon: ImageIcon, label: 'Media', path: '/rani-admin/media' },
  { icon: BarChart3, label: 'Analytics', path: '/rani-admin/analytics' },
  { icon: Settings, label: 'Settings', path: '/rani-admin/settings' },
];

export const AdminLayout = () => {
  const { admin, logout } = useAdmin();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (!admin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-surface border-r border-gray-200 transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b">
            <img src={LOGO_URL} alt="Rani Store" className="h-10" />
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-2">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/rani-admin'}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors mb-1 ${
                    isActive
                      ? 'bg-primary text-white'
                      : 'text-muted-foreground hover:bg-gray-100 hover:text-foreground'
                  }`
                }
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* User */}
          <div className="border-t p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">{admin.username}</p>
                <p className="text-xs text-muted-foreground">{admin.role}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
                className="text-muted-foreground hover:text-red-500"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-gray-200 flex items-center px-4 lg:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden mr-4"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold text-[#1A1625]">Admin Dashboard</h1>
          <div className="ml-auto flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('/', '_blank')}
            >
              View Store
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      <Toaster position="top-right" />
    </div>
  );
};

export default AdminLayout;
